<template>
	<section class="streak-card" aria-label="Daily streak">
		<!-- Hero Section -->
		<div class="streak-hero">
			<!-- Flame + Count -->
			<div class="streak-hero-left">
				<img
					src="../../assets/newdesign/landing/streak.svg"
					alt="Streak flame"
					class="streak-flame"
				/>
				<div class="streak-text-block">
					<strong
						class="streak-count"
						:aria-label="`${streakCount} Day streak`"
						>{{ streakCount }}</strong
					>
					<span class="streak-label">Day Streak</span>
				</div>
			</div>

			<!-- Right: View button -->
			<div v-if="showViewButton" class="streak-hero-right">
				<button
					@click="$emit('view-streak')"
					class="streak-view-btn border-0 bg-transparent p-0"
					aria-label="View streak details"
				>
					<img
						src="../../assets/newdesign/landing/white-right-arrow.svg"
						alt="View streak"
					/>
				</button>
			</div>
			<div id="hideinlaptop" class="st-grey-dragger" aria-hidden="true"></div>
		</div>

		<!-- Week Grid -->
		<div class="streak-week" aria-label="Weekly activity">
			<div
				v-for="(day, i) in weekDays"
				:key="i"
				class="streak-day-col"
				:class="{ 'streak-day-active': day.label === today }"
			>
				<time
					class="streak-day-label"
					:class="{ 'active-label': day.label === today }"
					:aria-current="day.label === today ? 'date' : undefined"
				>
					{{ day.label }}
				</time>
				<!-- Completed day -->
				<div
					v-if="day.status === 'ok'"
					class="streak-day-box streak-day-ok streak-day-clickable"
					:aria-label="day.label + ' completed'"
					@click="goToDayOnStreaksPage(i)"
				>
					<img
						src="../../assets/newdesign/landing/ok.svg"
						alt="Completed"
						class="streak-day-icon"
					/>
				</div>
				<!-- Missed day -->
				<div
					v-else-if="day.status === 'miss'"
					class="streak-day-box streak-day-miss streak-day-clickable"
					:aria-label="day.label + ' missed'"
					@click="goToDayOnStreaksPage(i)"
				>
					<img
						src="../../assets/newdesign/landing/notOk.svg"
						alt="Missed"
						class="streak-day-icon"
					/>
				</div>
				<!-- Today -->
				<div
					v-else-if="day.status === 'today'"
					class="streak-day-box streak-day-today streak-day-clickable"
					aria-label="Today"
					@click="goToDayOnStreaksPage(i)"
				></div>
				<!-- Future / empty day -->
				<div
					v-else
					class="streak-day-box streak-day-empty"
					:aria-label="day.label + ' upcoming'"
				></div>
			</div>
		</div>

		<!-- Date + Activities Row -->
		<div class="streak-date-row" v-if="!hideStats">
			<div class="streak-date-text">
				<span class="streak-date-day">{{ formattedDay }}</span
				><sup class="streak-date-ordinal">{{ dayOrdinal }}</sup>
				<span class="streak-date-month-first"
					>&nbsp;{{ formattedMonthParts.firstChar }}</span
				><span class="streak-date-month-rest">{{
					formattedMonthParts.restChars
				}}</span>
				<span class="streak-date-year"
					>&nbsp;{{ formattedMonthParts.year }}</span
				>
			</div>
			<div class="streak-activities-count">
				{{ todayTotalCount }} ACTIVITIES
			</div>
		</div>

		<!-- Stats List -->
		<div
			class="streak-stats"
			aria-label="Today's activity summary"
			v-if="!hideStats"
		>
			<div v-for="(stat, i) in visibleStats" :key="i" class="streak-stat-row">
				<div class="streak-stat-left">
					<div class="streak-stat-icon-wrap">
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<circle cx="10" cy="10" r="8" fill="#FFFFFF" />
							<path
								d="M6.5 10L8.5 12L13 7.5"
								stroke="#263238"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</div>
					<p class="streak-stat-text mb-0">
						<strong v-if="stat.showCount">{{ stat.count }}</strong>
						<span v-if="stat.showCount">&nbsp;</span>
						<span>{{ stat.label }}</span>
					</p>
				</div>
				<div
					v-if="i === visibleStats.length - 1 && remainingCount > 0"
					class="streak-more-inline"
				>
					<span class="streak-more-link" @click="goToStreaksPage"
						>+ {{ remainingCount }} more</span
					>
				</div>
			</div>
			<p v-if="!stats.length" class="streak-stat-text mb-0">
				No streak activities yet today
			</p>
		</div>
	</section>
</template>

<script>
import { getProfileStreak } from "../../utils/apiFunctions.js";

export default {
	name: "StreakCard",
	emits: ["view-streak"],
	props: {
		showViewButton: {
			type: Boolean,
			default: true,
		},
		hideStats: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			today: "",
			weekStart: null,
			streakCount: 0,
			weekDays: [
				{ label: "Sun", status: "empty" },
				{ label: "Mon", status: "empty" },
				{ label: "Tue", status: "empty" },
				{ label: "Wed", status: "empty" },
				{ label: "Thu", status: "today" },
				{ label: "Fri", status: "empty" },
				{ label: "Sat", status: "empty" },
			],
			stats: [],
			todayTotalCount: 0,
			maxVisibleStats: 3,
		};
	},
	computed: {
		formattedDay() {
			const now = new Date();
			return now.getDate();
		},
		dayOrdinal() {
			const day = new Date().getDate();
			if (day >= 11 && day <= 13) return "th";
			switch (day % 10) {
				case 1:
					return "st";
				case 2:
					return "nd";
				case 3:
					return "rd";
				default:
					return "th";
			}
		},
		formattedMonthParts() {
			const dateStr = new Date().toLocaleDateString("en-US", {
				month: "long",
				year: "numeric",
			});
			const parts = dateStr.split(" ");
			const month = parts[0] || "";
			const year = parts[1] || "";
			return {
				firstChar: month.charAt(0),
				restChars: month.slice(1),
				year: year,
			};
		},
		visibleStats() {
			return this.stats.slice(0, this.maxVisibleStats);
		},
		remainingCount() {
			return Math.max(0, this.stats.length - this.maxVisibleStats);
		},
	},
	created() {
		this.today = new Date().toLocaleDateString("en-US", { weekday: "short" });
		this.getWeeklyStreak();
	},
	methods: {
		goToStreaksPage() {
			this.$router.push({ path: "/streaks" });
		},
		goToDayOnStreaksPage(dayIndex) {
			if (!this.weekStart) return;
			const d = new Date(this.weekStart);
			d.setDate(this.weekStart.getDate() + dayIndex);
			const dateStr = this.getDayKey(d);
			if (this.$route.path === "/streaks") {
				// Already on the streaks page — emit directly, no route change
				this.$emit("day-selected", dateStr);
			} else {
				this.$router.push({ path: "/streaks", query: { date: dateStr } });
			}
		},
		getDayKey(date) {
			return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
				2,
				"0"
			)}-${String(date.getDate()).padStart(2, "0")}`;
		},
		getStatusForDate(dayDate, todayDate, count) {
			if (this.getDayKey(dayDate) === this.getDayKey(todayDate)) {
				return count > 0 ? "ok" : "today";
			}

			if (dayDate > todayDate) {
				return "empty";
			}

			return count > 0 ? "ok" : "miss";
		},
		getActivityLabel(item) {
			const count = Number(item?.count || 0);
			const logType = Number(item?.logType);

			switch (logType) {
				case 41:
					return "Signed in";
				case 42:
					return count === 1 ? "assessment completed" : "assessments completed";
				case 43:
					return count === 1
						? "Mock Interview Completed"
						: "Mock Interviews Completed";
				case 44:
					return count === 1
						? "job application submitted"
						: "job applications submitted";
				case 45:
					return count === 1 ? "interview completed" : "interviews completed";
				case 46:
					return count === 1 ? "resume updated" : "resumes updated";
				case 47:
					return count === 1
						? "panelist application submitted"
						: "panelist applications submitted";
				case 48:
					return count === 1 ? "Skill Practiced" : "Skills Practiced";
				case 49:
					return count === 1 ? "Skill Achieved" : "Skills Achieved";
				case 25:
				case 26:
					return "Secondary Profile Created";
				case 37:
					return "New Profile Created";
				case 50:
					return "New Profile Created";
				case 51:
					return count === 1 ? "Interview Conducted" : "Interviews Conducted";
				case 52:
					return count === 1 ? "Course Published" : "Courses Published";
				case 53:
					return count === 1 ? "Course Completed" : "Courses Completed";
				default:
					return count === 1 ? "activity" : "activities";
			}
		},
		toTitleCase(str) {
			if (!str) return "";
			return String(str).replace(/\b\w/g, (c) => c.toUpperCase());
		},
		async getWeeklyStreak() {
			try {
				const now = new Date();
				const todayDate = new Date(
					now.getFullYear(),
					now.getMonth(),
					now.getDate()
				);
				const weekStart = new Date(todayDate);
				weekStart.setDate(todayDate.getDate() - todayDate.getDay());
				this.weekStart = new Date(weekStart);

				const payload = {
					startDate: `${weekStart.getFullYear()}-${String(
						weekStart.getMonth() + 1
					).padStart(2, "0")}`,
					duration: 2,
				};

				const resp = await getProfileStreak(payload);
				if (resp.status !== 200 || !resp.data?.data?.data) {
					return;
				}

				const streakPayload = resp.data?.data || {};
				const rows = streakPayload.data || [];
				const countsByDate = rows.reduce((acc, item) => {
					const key = `${item.year}-${String(item.month).padStart(
						2,
						"0"
					)}-${String(item.day).padStart(2, "0")}`;
					acc[key] = item.count || 0;
					return acc;
				}, {});

				this.streakCount =
					streakPayload.currentStreak ||
					streakPayload.streakCount ||
					streakPayload.currentStreakCount ||
					this.streakCount;

				const todayActivity = Array.isArray(streakPayload.todayActivity)
					? streakPayload.todayActivity
					: [];

				// Configuration: which log types should show numeric counts vs be presented as a single event
				const COUNT_TYPES = new Set([42, 43, 44, 45, 52]);
				const NO_COUNT_TYPES = new Set([33, 37, 47, 50, 25, 26, 41]);
				const NO_DISPLAY_TYPES = new Set([]);
				const RESUME_START = 1;
				const RESUME_END = 21;

				let todayTotal = 0;
				const seenNoCount = new Set();
				const statsMap = new Map();

				for (const item of todayActivity) {
					if (!item) continue;
					const raw = Number(item?.count);
					const logTypeRaw = Number(item?.logType);
					const labelFromAPI = item?.label;
					const triggeredLogCount = Array.isArray(item?.triggeredLogTypes)
						? item.triggeredLogTypes.length
						: 0;
					const doneFlag = item?.done === true;
					const triggeredCount =
						Number.isFinite(raw) && raw > 0
							? raw
							: triggeredLogCount > 0
							? triggeredLogCount
							: doneFlag
							? 1
							: 0;

					// skip activities that were not triggered and have no counts
					if (triggeredCount === 0) {
						continue;
					}

					const labelLower = (labelFromAPI || "").toLowerCase();
					let showCount = false;
					let count = triggeredCount;
					let label = labelFromAPI || null;
					let groupKey = null;
					const logType = Number.isFinite(Number(logTypeRaw))
						? Number(logTypeRaw)
						: null;

					// If login (41) — count for eligibility but don't display it
					if (NO_DISPLAY_TYPES.has(logType)) {
						todayTotal += triggeredCount;
						continue;
					}

					if (logType !== null) {
						if (logType >= RESUME_START && logType <= RESUME_END) {
							groupKey = "resume";
							showCount = false;
							count = 1;
							label = "Resume updated";
						} else if (NO_COUNT_TYPES.has(logType)) {
							if (logType === 25 || logType === 26) {
								groupKey = "secondary_profile";
							} else {
								groupKey = `lt_${logType}`;
							}
							showCount = false;
							count = 1;
							label = label || this.getActivityLabel({ logType, count: 1 });
						} else if (COUNT_TYPES.has(logType)) {
							groupKey = `lt_${logType}`;
							showCount = true;
							count = triggeredCount;
							label = label || this.getActivityLabel({ logType, count });
						} else {
							groupKey = `lt_${logType}`;
							showCount = triggeredCount > 0;
							count = triggeredCount;
							label = label || this.getActivityLabel({ logType, count });
						}
					} else {
						if (/resume|cv|cvs?/.test(labelLower)) {
							groupKey = "resume";
							showCount = false;
							count = 1;
							label = "Resume updated";
						} else if (/new profile/.test(labelLower)) {
							groupKey = "new_profile";
							showCount = false;
							count = 1;
							label = "New profile created";
						} else if (/secondary profile|secondary/.test(labelLower)) {
							groupKey = "secondary_profile";
							showCount = false;
							count = 1;
							label = "Secondary profile created";
						} else if (/video snapshot|snapshot/.test(labelLower)) {
							groupKey = "video_snapshot";
							showCount = false;
							count = 1;
							label = "Video snapshot created";
						} else if (
							/panelist|applied as panelist|panelist application/.test(
								labelLower
							)
						) {
							groupKey = "panelist";
							showCount = false;
							count = 1;
							label = "Panelist application submitted";
						} else if (/assessment/.test(labelLower)) {
							groupKey = "label_assessment";
							showCount = true;
							count = triggeredCount;
							label = this.getActivityLabel({ logType: 42, count });
						} else {
							showCount = triggeredCount > 0;
							count = triggeredCount;
							groupKey =
								"label_" + (labelFromAPI || Math.random()).toLowerCase();
							label = label || this.getActivityLabel({ logType: null, count });
						}
					}

					// Contribute to today's total (count-types add their numeric value; no-count types count as 1 once per group)
					if (showCount) {
						todayTotal += count;
					} else {
						if (!seenNoCount.has(groupKey)) {
							todayTotal += 1;
							seenNoCount.add(groupKey);
						}
					}

					// Aggregate into statsMap (combine like labels)
					const mapKey = groupKey || (label || "").toLowerCase();
					const existing = statsMap.get(mapKey);
					if (existing) {
						if (existing.showCount) {
							existing.count += count;
						}
					} else {
						statsMap.set(mapKey, { count, label, showCount, logType });
					}
				}

				// Build final stats array with correct pluralisation for count-types
				let finalStats = Array.from(statsMap.values()).map((entry) => {
					if (entry.showCount && Number.isFinite(entry.logType)) {
						const cnt = entry.count;
						const lbl = this.getActivityLabel({
							logType: entry.logType,
							count: cnt,
						});
						return {
							count: cnt,
							label: lbl,
							showCount: true,
							logType: entry.logType,
						};
					} else if (entry.showCount) {
						return {
							count: entry.count,
							label: entry.label,
							showCount: true,
							logType: entry.logType,
						};
					}
					return {
						count: entry.count,
						label: entry.label,
						showCount: false,
						logType: entry.logType,
					};
				});

				// Enforce no-count rules for specific types/labels (resume updates, secondary profiles, video snapshots, panelist)
				finalStats = finalStats.map((s) => {
					const lbl = (s.label || "").toLowerCase();
					const lt = Number.isFinite(s.logType) ? s.logType : null;
					const isResume = lt >= RESUME_START && lt <= RESUME_END;
					const isNewProfile =
						/new profile/.test(lbl) || lt === 50 || lt === 37;
					const isSecondary =
						/secondary profile|secondary/.test(lbl) || lt === 25 || lt === 26;
					const isVideoSnapshot =
						/video snapshot|snapshot/.test(lbl) || lt === 33;
					const isPanelist =
						/panelist|applied as panelist|panelist application/.test(lbl) ||
						lt === 47;
					if (isResume) {
						return { count: 1, label: "Resume updated", showCount: false };
					}
					if (isNewProfile) {
						return { count: 1, label: "New profile created", showCount: false };
					}
					if (isSecondary) {
						return {
							count: 1,
							label: "Secondary profile created",
							showCount: false,
						};
					}
					if (isVideoSnapshot) {
						return {
							count: 1,
							label: "video snapshot created",
							showCount: false,
						};
					}
					if (isPanelist) {
						return {
							count: 1,
							label: "panelist application submitted",
							showCount: false,
						};
					}
					return s;
				});

				const todayKey = this.getDayKey(todayDate);
				const heatmapCount = countsByDate[todayKey] || 0;
				if (heatmapCount > todayTotal) {
					const diff = heatmapCount - todayTotal;
					finalStats.push({
						count: diff,
						label: "Signed in",
						showCount: false,
						logType: 41,
					});
					todayTotal = heatmapCount;
				}

				if (todayTotal > 0) {
					countsByDate[todayKey] = Math.max(
						countsByDate[todayKey] || 0,
						todayTotal
					);
				}
				this.todayTotalCount = todayTotal;
				this.stats = finalStats.map((s) => ({
					...s,
					label: this.toTitleCase(s.label),
				}));

				this.weekDays = Array.from({ length: 7 }, (_, idx) => {
					const dayDate = new Date(weekStart);
					dayDate.setDate(weekStart.getDate() + idx);
					const dayLabel = dayDate.toLocaleDateString("en-US", {
						weekday: "short",
					});
					const dayKey = this.getDayKey(dayDate);
					const count = countsByDate[dayKey] || 0;

					return {
						label: dayLabel,
						status: this.getStatusForDate(dayDate, todayDate, count),
					};
				});
			} catch (error) {
				console.log(error);
			}
		},
		goToStreaksPage() {
			this.$router.push("/streaks");
		},
	},
};
</script>

<style scoped lang="scss">
/* ── Card shell ── */
.streak-card {
	background: #263238;
	border-radius: 12px;
	padding: 16px 16px 24px 16px;
	width: 100%;
	overflow: hidden;
	border: 1px solid #e2e8f0;
	flex-shrink: 0;
}

/* ── Hero (flame + count) ── */
.streak-hero {
	display: flex;
	align-items: start;
	justify-content: space-between;
	position: relative;
}

.streak-hero-left {
	display: flex;
	align-items: center;
	gap: 12px;
}

.streak-flame {
	width: 56px;
	height: 56px;
	flex-shrink: 0;
}

.streak-text-block {
	display: flex;
	flex-direction: column;
}

.streak-count {
	font-size: 32px;
	font-weight: 700;
	font-style: italic;
	color: #ffffff;
	line-height: 1;
	letter-spacing: -1px;
	font-family: "Montserrat", sans-serif;
}

.streak-label {
	font-size: 16px;
	font-weight: 500;
	color: #e0e0e0;
	margin-top: 2px;
	letter-spacing: 0.3px;
	font-family: "Montserrat", sans-serif;
}

.streak-hero-right {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 8px;
}

/* ── Week grid ── */
.streak-week {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	padding: 10px;
	gap: 4px;
	border: 1px solid #e2e8f026;
	background: radial-gradient(
		100% 100% at 50.05% 0%,
		rgba(255, 255, 255, 0.2) 0%,
		rgba(32, 99, 155, 0.12) 100%
	);
	box-shadow: 0px 4px 6px -4px #581c870d, 0px 10px 15px -3px #581c870d;
	border-radius: 12px;
	backdrop-filter: blur(12px);
	margin-block: 14px;
}

.streak-day-col {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	flex: 1;
}

.streak-day-label {
	font-size: 10px;
	font-weight: 400;
	color: #e0e0e0b2;
	letter-spacing: 0.2px;
	font-family: "Inter", sans-serif;
}

.active-label {
	color: #ffffff;
	font-weight: 800;
}

.streak-day-box {
	width: 36px;
	height: 36px;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.streak-day-icon {
	width: 31px;
	height: 31px;
}

/* Completed — orange gradient (comes from ok.svg itself, box is transparent) */
.streak-day-ok {
	background: transparent;
}

/* Missed — grey box with X icon (notOk.svg is grey) */
.streak-day-miss {
	background: transparent;
}

/* Future — white / light empty square */
.streak-day-empty {
	background: #ffffff;
	border: 1.5px solid #e2e8f0;
}
.streak-day-today {
	background: transparent;
	border: 1px solid #fe9842;
}
/* ── Date + Activities Row ── */
.streak-date-row {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	padding: 0 10px;
	margin-bottom: 12px;
}

.streak-date-text {
	font-family: "Montserrat", sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 100%;
	letter-spacing: 0%;
	font-variant: small-caps;
	background: linear-gradient(90deg, #fcca6e, #ff953f);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.streak-date-day {
	font-family: "Montserrat", sans-serif;
	font-weight: 500;
	font-size: 20px;
	line-height: 100%;
	letter-spacing: 0%;
	font-variant: small-caps;
	background: linear-gradient(90deg, #fcca6e, #ff953f);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.streak-date-ordinal {
	font-family: "Montserrat", sans-serif;
	font-weight: 600;
	line-height: 100%;
	letter-spacing: 0%;
	font-variant: small-caps;
	font-size: 8px;
	position: relative;
	top: -8px;
	vertical-align: baseline;
	text-transform: uppercase;
	background: linear-gradient(90deg, #fcca6e, #ff953f);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.streak-date-month-first,
.streak-date-year {
	font-family: "Montserrat", sans-serif;
	font-weight: 500;
	font-size: 20px;
	line-height: 100%;
	letter-spacing: 0%;
	text-transform: uppercase;
	background: linear-gradient(90deg, #fcca6e, #ff953f);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.streak-date-month-rest {
	font-family: "Montserrat", sans-serif;
	font-weight: 500;
	font-size: 16px;
	line-height: 100%;
	letter-spacing: 0%;
	text-transform: uppercase;
	background: linear-gradient(90deg, #fcca6e, #ff953f);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

.streak-activities-count {
	font-family: "Montserrat", sans-serif;
	font-weight: 700;
	font-size: 13px;
	line-height: 100%;
	letter-spacing: 0%;
	font-variant: small-caps;
	color: rgba(226, 232, 240, 0.6);
}

/* ── Stats ── */
.streak-stats {
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 4px 10px 0;
}

.streak-stat-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
}

.streak-stat-left {
	display: flex;
	align-items: center;
	gap: 12px;
}

.streak-stat-icon-wrap {
	width: 20px;
	height: 20px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.streak-stat-text {
	font-size: 14px;
	font-weight: 400;
	color: #ffffff;
	letter-spacing: 1px;
	font-family: "Inter", sans-serif;

	strong {
		font-weight: 700;
		color: #ffffff;
	}
}

.streak-more-inline {
	display: flex;
	align-items: center;
	justify-content: flex-end;
}

.streak-more-link {
	font-family: "Montserrat", sans-serif;
	font-weight: 600;
	font-size: 14px;
	color: #ffffff;
	cursor: pointer;
	transition: opacity 0.2s ease;
	letter-spacing: 0.5px;
	text-decoration: underline;
	text-underline-offset: 3px;
	text-decoration-thickness: 1.5px;
}

.streak-more-link:hover {
	opacity: 0.8;
}

.streak-day-clickable {
	cursor: pointer;
	transition: opacity 0.15s ease, transform 0.15s ease;
}

.streak-day-clickable:hover {
	opacity: 0.8;
	transform: scale(1.08);
}
@media (max-width: 1028px) {
	.streak-card {
		width: 100%;
		position: absolute;
		bottom: calc(env(safe-area-inset-bottom));
		left: 0;
		z-index: 11;
		border-radius: 12px 12px 0 0px;
		padding: 50px 16px 16px 16px;
	}
	.st-grey-dragger {
		width: 115px;
		height: 4px;
		background: #e2e8f0;
		border-radius: 2px;
		position: absolute;
		top: -28px;
		left: 50%;
		transform: translateX(-50%);
	}
}
</style>
